import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { AdditionalDataDto, AdditionalDataFieldDto } from 'api/additional-data/models/AdditionalDataDto';
import Loading from 'common/services/Loading';
import AdditionalDataService from 'api/additional-data/AdditionalDataService';
import Logger from 'common/services/Logger';
import Button from 'common/components/button/Button';
import styles from './AdditionalDataDetails.module.scss';
import InputError from 'common/components/inputError/InputError';
import Label from 'common/components/label/Label';
import FormItem from 'common/components/formItem/FormItem';
import { useForm } from 'react-hook-form';
import { LOGGER_LOG_TYPE, removeAccents } from 'Config';
import GreyArrow from 'assets/svg/Arrow-grey.svg';
import React, { useEffect, useState } from 'react';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { useHistory, useParams } from 'react-router-dom';
import ContentManagement from 'common/components/contentManagement/ContentManagement';
import InputController from 'common/components/input/InputController';
import LanguagesTab from 'common/components/languagesTabs/LanguagesTab';
import { LanguageDto } from 'api/languages/models/LanguageDto';
import LanguagesService from 'api/languages/LanguagesService';
import { AdditionalDataTypeEnum } from 'api/additional-data/enums/AdditionalDataTypeEnum';
import { AdditionalDataLinkEnum } from 'api/additional-data/enums/AdditionalDataLinkEnum';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { Col, Row } from 'react-flexbox-grid';
import SelectController from 'common/components/select/SelectController';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import { SelectValueLabelDto } from 'api/common/models/SelectValueLabelDto';
import ToggleSwitch from 'common/components/toggleSwitch/ToggleSwitch';

type Props = {
    companyId: string;
};

const AdditionalDataDetails = () => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const { childrenId: additionalDataId, tabType, id: companyId } = useParams<{ childrenId: string, tabType: string, id: string }>();
    const [additionalData, setAdditionalData] = useState<AdditionalDataDto>();
    const [isDetails, setIsDetails] = useState<boolean>(tabType === 'details');
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<AdditionalDataDto | null>(null);
    const history = useHistory();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasAdditionalDataWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_COMPANIES_WRITE'], 'OR');    
    const form = useForm<AdditionalDataDto>({ shouldUnregister: false });
    const { handleSubmit, errors, setValue } = form;
    const [languages, setLanguages] = useState<LanguageDto[]>([]);
    const [selectedLanguageIndex, setSelectedLanguageIndex] = useState(0);
    const [typeList, setTypeList] = useState<SelectValueLabelDto[]>([]);
    const [linkList, setLinkList] = useState<SelectValueLabel[]>([]);
    
    const goToList = () => {
        history.push(`/companies/details/${companyId}/additionalData`);
    };

    const onCancel = () => {
        if (isDetails) {
            goToList();
        } else if (!additionalDataId) {
            goToList();
        }
        else {
             history.push(`/companies/details/${companyId}/additionaldata/details/${additionalDataId}`);             
        }
    };

    const getData = async () => {

        Loading.show();
        try {
            let list: AdditionalDataFieldDto[] = [];
            const listLanguages = await LanguagesService.getAll();
            listLanguages[0].languageTabVisited = true;
            setLanguages(listLanguages);
            const model = additionalDataId ? await AdditionalDataService.getById(additionalDataId) : { type: `${AdditionalDataTypeEnum.TEXT}`, link: `${AdditionalDataLinkEnum.VEHICLE}`, fields: [], order: 1, showInListing: false };
            setAdditionalData(model);
            list = listLanguages.map(lang => {
                const item = model.fields?.find(x => x.languageId === lang.id);
                if (item) {
                    return item;
                }
                return {
                    id: undefined,
                    designation: '',
                    languageId: lang.id,
                };
            });
            model.fields = list;
            form.reset(model);
            Loading.hide();
        } catch (error) {
            addToast(t('common.messages.error_load_info'), {
                appearance: 'error',
            });

            Logger.error(
                LOGGER_LOG_TYPE.REQUEST,
                `Couldn't get catalog additional data`,
                error
            );
            Loading.hide();
        }
    }

    useEffect(() => {        
        getData();
    }, [isDetails]);

    useEffect(() => {
        setIsDetails(tabType === 'details')        
    }, [tabType]);

    useEffect(() => {        
        const types = [{ value: AdditionalDataTypeEnum.NUMBER, label: t('additional_data.type.NUMBER') },
        { value: AdditionalDataTypeEnum.TEXT, label: t('additional_data.type.TEXT') },
        { value: AdditionalDataTypeEnum.DATE, label: t('additional_data.type.DATE') },
        { value: AdditionalDataTypeEnum.BOOLEAN, label: t('additional_data.type.BOOLEAN') }]
        setTypeList(types.sort((a, b) => a.label.localeCompare(b.label)));
        const links = [{ value: AdditionalDataLinkEnum.VEHICLE, label: t('additional_data.link.VEHICLE') },
        { value: AdditionalDataLinkEnum.INSURANCE_CONTRACT, label: t('additional_data.link.INSURANCE_CONTRACT') },
        { value: AdditionalDataLinkEnum.MAINTENANCE_CONTRACT, label: t('additional_data.link.MAINTENANCE_CONTRACT') },
        { value: AdditionalDataLinkEnum.USE_CONTRACT, label: t('additional_data.link.USE_CONTRACT') },
        { value: AdditionalDataLinkEnum.VERIFICATION, label: t('additional_data.link.VERIFICATION') }]
        setLinkList(links.sort((a, b) => a.label.localeCompare(b.label)));
    }, []);

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }
        try {
            await AdditionalDataService.remove({ ...itemToRemove, companyId });
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
            onCancelRemove();
            goToList();
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete additional data`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    const showRemoveItemDialog = async (item: AdditionalDataDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onSubmit = async (formData: AdditionalDataDto) => {
        try {
            if (!hasAdditionalDataWritePolicy) { return; }
            Loading.show();            
            if (additionalData != null && additionalDataId) {                                
                 await AdditionalDataService.update({ ...formData, companyId });
            } else {
                await AdditionalDataService.create({ ...formData, companyId });
            }
            Loading.hide();
            addToast(t('common.messages.record_save_success'), {
                appearance: 'success',
            });
            goToList();
        } catch (error) {
            addToast(t('common.messages.record_save_error'), {
                appearance: 'error',
            });

            Logger.error(
                LOGGER_LOG_TYPE.REQUEST,
                `Couldn't create or update additional data`,
                error
            );
            Loading.hide();
        }
    };


    const notSubmitted = async (errorsInForm?: any) => {
        for (let i = 0; i < languages.length; i++) {
            const language = languages[i];
            language.invalid = Boolean((errorsInForm?.list || [])[i]) || (!language.languageTabVisited);
        }
    };

    const copyLanguage = (destinationIndex: number, langToCopyId: string) => {
        const sourceItem = form.getValues('fields')?.find(x => x.languageId === langToCopyId);
        if (sourceItem) {
            form.setValue(`fields.${destinationIndex}.designation` as 'list.0.designation', sourceItem.designation);
        }
    }

    const onLanguageChange = (lang: LanguageDto, i: number) => {
        setLanguages([
            ...languages.map(l => {
                if (l.id === lang.id) {
                    return {
                        ...l,
                        languageTabVisited: true
                    }
                }
                return l;
            }),
        ]);

        setSelectedLanguageIndex(i);
    }

    return (
        <div className={styles.container}>
            <div className={styles.pageNameContainer}>
                <div className={styles.pageNameContent} onClick={() => goToList()} >
                    <img className={styles.pageNameContentImage} src={GreyArrow} />
                    <span className={styles.pageNameContentText}>
                        {t('additional_data.title')}
                    </span>
                </div>
            </div>
            <form onSubmit={handleSubmit(onSubmit, notSubmitted)} className={styles.form}>
                <Row>
                    <Col xs={12} md={6} lg={4}>
                        <FormItem>
                            <Label className={styles.label}>{t('additional_data.link.title')} {!isDetails ? '*' : ''}</Label>
                            <SelectController
                                form={form}
                                name='link'
                                menuPortalTarget={document.querySelector('body')}
                                options={linkList}
                                placeholder={t('additional_data.link.title')}
                                rules={{ required: true }}
                                isDisabled={isDetails || !!additionalDataId}
                                filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                onChangeSelect={(data: SelectValueLabel) => {
                                    setValue('link', data ? data.value : null);
                                }}

                            />
                            <InputError error={errors.link} />
                        </FormItem>
                    </Col>
                    <Col xs={12} md={6} lg={3}>
                        <FormItem>
                            <Label className={styles.label}>{t('additional_data.type.title')} {!isDetails ? '*' : ''}</Label>
                            <SelectController
                                form={form}
                                name='type'
                                menuPortalTarget={document.querySelector('body')}
                                options={typeList}
                                placeholder={t('additional_data.type.title')}
                                rules={{ required: true }}                                
                                isDisabled={isDetails || !!additionalDataId}
                                filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                onChangeSelect={(data: SelectValueLabel) => {                                    
                                    setValue('type', data ? data.value : null);
                                }}                            />
                            <InputError error={errors.type} />
                        </FormItem>
                    </Col>
                    
                    <Col xs={12} md={6} lg={2}>
                    <FormItem>
                        <Label>{t('additional_data.order')}</Label>
                        <InputController
                            form={form as any}
                            name={'order'}
                            placeholder={t('additional_data.order')}
                            disabled={isDetails}
                            type='number' />
                        <InputError error={errors?.order} />
                    </FormItem>
                    </Col>
                    <Col xs={12} md={6} lg={3}>
                        <FormItem>
                            <Label>{t('additional_data.show_in_listing')}</Label>
                            <ToggleSwitch
                                form={form as any}
                                name='showInListing'
                                isDetails={isDetails}
                            />
                        </FormItem>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <LanguagesTab
                            selectedLanguageIndex={selectedLanguageIndex}
                            languages={languages}
                            onChange={onLanguageChange}
                        >
                            <div className={styles.containerWrapper}>
                                {languages.map((lang, index) => {
                                    const keyName = `fields.${index}`;
                                    const itemErrors = (form.formState.errors.fields || [])[index];

                                    return <div key={lang.id + '_' + index}
                                        className={`${index != selectedLanguageIndex && styles.hideTab}`}>
                                        {!isDetails && <div className={styles.contentManagement}><ContentManagement
                                            options={languages.filter(x => x.id != lang.id).map(x => ({ label: x.name, value: x.id }))}
                                            onCopy={lang => copyLanguage(selectedLanguageIndex, lang)}
                                            isInnerContainer={true}
                                        />
                                        </div>}

                                        <div key={`form_${index}`} className={styles.formContent}>
                                            <FormItem>
                                                <Label>{t('additional_data.designation')}</Label>
                                                <InputController
                                                    form={form as any}
                                                    name={`${keyName}.designation`}
                                                    placeholder={t('additional_data.designation')}
                                                    disabled={isDetails}
                                                    rules={{ required: true, maxLength: 200 }}
                                                />
                                                <InputError error={itemErrors?.designation} />
                                            </FormItem>

                                        </div>
                                    </div>
                                })
                                }
                            </div>
                        </LanguagesTab>
                    </Col>
                </Row>
                <div className={styles.buttonContainer}>
                    <Button preset={'secondary'}
                        type='button'
                        text={t('common.cancel')}
                        onClick={() => onCancel()} />
                     {isDetails && additionalDataId && hasAdditionalDataWritePolicy &&
                        <Button
                            type="button"
                            text={t('common.remove')}
                            preset={'danger'}
                            onClick={() => showRemoveItemDialog({ id: additionalDataId } as AdditionalDataDto)} />
                    }
                    {isDetails && hasAdditionalDataWritePolicy && <Button type='button' text={t('common.edit')} onClick={() => { history.push(`/companies/details/${companyId}/additionaldata/edit/${additionalDataId}`); }} />}
                    {!isDetails && hasAdditionalDataWritePolicy && <Button type='submit' text={t('common.save')} />}
                </div>

            </form>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('common.messages.remove_record')} />
        </div>
    );
};

export default AdditionalDataDetails;
